import React, { useState, useEffect, useRef } from 'react'
import logo from '../../Assets/logo.png'
import rapdureHeadLogo from '../../Assets/Rapdure_Head_Logo.png'

export const ComingSoon: React.FC = () => {
  const [email, setEmail] = useState<string>('')
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const betaRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement
            el.style.opacity = '1'
            el.style.transform = 'translateY(0)'
          }
        })
      },
      { threshold: 0.2 },
    )

    const els = document.querySelectorAll('.fade-in-up')
    els.forEach((el) => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  const handleSubmit = (): void => {
    const trimmed = email.trim()
    if (!trimmed || !trimmed.includes('@') || !trimmed.includes('.')) {
      setError(true)
      setTimeout(() => setError(false), 1500)
      return
    }

    // TODO: Replace with your actual backend endpoint
    // fetch('https://your-api.execute-api.us-east-2.amazonaws.com/beta-signup', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ email: trimmed })
    // });

    console.log('Beta signup:', trimmed)
    setSubmitted(true)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSubmit()
  }

  const scrollToBeta = (): void => {
    betaRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500;600&display=swap');

        .cs-root * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .cs-root {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
          color: #fff;
          overflow-x: hidden;
          background: #000;
        }

        /* ── Hero ── */
        .cs-hero {
          position: relative;
          width: 100%;
          height: 100vh;
          overflow: hidden;
        }

        .cs-hero-bg {
          position: absolute;
          inset: 0;
          background-image: url('landing_photo.jpg');
          background-size: cover;
          background-position: center;
          animation: csHeroZoom 20s ease-in-out infinite alternate;
        }

        @keyframes csHeroZoom {
          0% { transform: scale(1); }
          100% { transform: scale(1.06); }
        }

        .cs-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to bottom,
            rgba(0,0,0,0.35) 0%,
            rgba(0,0,0,0.15) 40%,
            rgba(0,0,0,0.25) 70%,
            rgba(0,0,0,0.7) 100%
          );
        }

        .cs-hero-content {
          position: relative;
          z-index: 2;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          padding: 3rem 2rem;
        }

        .cs-logo {
          max-width: 400px;
          width: 100%;
          height: auto;
          filter: drop-shadow(0 8px 24px rgba(0,0,0,0.9))
                  drop-shadow(0 4px 12px rgba(0,0,0,0.8))
                  drop-shadow(0 2px 6px rgba(0,0,0,0.7));
          animation: csFadeDown 1.2s ease-out;
        }

        @keyframes csFadeDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .cs-tagline-wrap {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .cs-tagline {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2.5rem, 6vw, 5rem);
          font-weight: 400;
          text-align: center;
          line-height: 1.3;
          color: #fff;
          text-shadow: 0 4px 20px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4);
          letter-spacing: 0.02em;
          animation: csFadeUp 1.4s ease-out 0.3s both;
        }

        @keyframes csFadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .cs-scroll-hint {
          animation: csFadeUp 1.6s ease-out 0.8s both;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: opacity 0.3s;
          background: none;
          border: none;
          color: #fff;
        }

        .cs-scroll-hint:hover { opacity: 0.7; }

        .cs-scroll-hint span {
          font-family: 'DM Sans', sans-serif;
          font-size: 0.75rem;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          opacity: 0.7;
        }

        .cs-scroll-arrow {
          width: 20px;
          height: 20px;
          border-right: 1.5px solid rgba(255,255,255,0.7);
          border-bottom: 1.5px solid rgba(255,255,255,0.7);
          transform: rotate(45deg);
          animation: csBounce 2s ease-in-out infinite;
        }

        @keyframes csBounce {
          0%, 100% { transform: rotate(45deg) translateY(0); }
          50% { transform: rotate(45deg) translateY(6px); }
        }

        /* ── Beta Section ── */
        .cs-beta {
          position: relative;
          background: #000;
          padding: 7rem 2rem 8rem;
          text-align: center;
          overflow: hidden;
        }

        .cs-beta::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(180,140,80,0.06) 0%, transparent 70%);
          pointer-events: none;
        }

        .cs-beta-inner {
          max-width: 640px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .cs-beta-label {
          display: inline-block;
          font-size: 0.7rem;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.5);
          border: 1px solid rgba(255,255,255,0.15);
          padding: 0.4rem 1.2rem;
          border-radius: 20px;
          margin-bottom: 2.5rem;
        }

        .cs-beta-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2rem, 5vw, 3.2rem);
          font-weight: 400;
          color: #fff;
          line-height: 1.35;
          margin-bottom: 3rem;
          letter-spacing: 0.01em;
        }

        .cs-beta-title em {
          font-style: italic;
        }

        /* Email form */
        .cs-email-form {
          display: flex;
          gap: 0;
          max-width: 480px;
          margin: 0 auto 1.5rem;
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 6px;
          overflow: hidden;
          transition: border-color 0.3s;
        }

        .cs-email-form:focus-within {
          border-color: rgba(255,255,255,0.35);
        }

        .cs-email-form.cs-error {
          border-color: rgba(255,100,100,0.5);
        }

        .cs-email-input {
          flex: 1;
          padding: 1rem 1.3rem;
          background: rgba(255,255,255,0.04);
          border: none;
          color: #fff;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          outline: none;
        }

        .cs-email-input::placeholder {
          color: rgba(255,255,255,0.3);
          font-weight: 300;
        }

        .cs-submit-btn {
          padding: 1rem 2rem;
          background: #fff;
          color: #000;
          border: none;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.85rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          cursor: pointer;
          transition: background 0.3s;
          white-space: nowrap;
        }

        .cs-submit-btn:hover { background: #e0e0e0; }
        .cs-submit-btn:active { transform: scale(0.98); }

        .cs-privacy {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.3);
          font-weight: 300;
        }

        /* Success */
        .cs-success {
          animation: csFadeUp 0.5s ease-out;
        }

        .cs-check-icon {
          width: 48px;
          height: 48px;
          border: 2px solid rgba(255,255,255,0.5);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.2rem;
          font-size: 1.2rem;
        }

        .cs-success-title {
          font-family: 'Playfair Display', serif;
          font-size: 1.4rem;
          color: #fff;
          margin-bottom: 0.5rem;
        }

        .cs-success-sub {
          font-size: 0.9rem;
          color: rgba(255,255,255,0.45);
          font-weight: 300;
        }

        /* Divider */
        .cs-divider {
          width: 60px;
          height: 1px;
          background: rgba(255,255,255,0.12);
          margin: 0 auto;
        }

        /* ── Bottom Section ── */
        .cs-bottom {
          background: #000;
          padding: 6rem 2rem 8rem;
          text-align: center;
          position: relative;
        }

        .cs-bottom-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2rem, 5vw, 3.5rem);
          font-weight: 400;
          color: #fff;
          letter-spacing: 0.05em;
          line-height: 1.4;
        }

        .cs-head-logo-left {
          position: absolute;
          left: 5%;
          top: 50%;
          transform: translateY(-50%);
          max-width: 150px;
          height: auto;
          opacity: 0.9;
        }

        .cs-head-logo-right {
          position: absolute;
          right: 5%;
          top: 50%;
          transform: translateY(-50%) scaleX(-1);
          max-width: 150px;
          height: auto;
          opacity: 0.9;
        }

        /* Footer */
        .cs-footer {
          background: #000;
          border-top: 1px solid rgba(255,255,255,0.06);
          padding: 2.5rem 2rem;
          text-align: center;
        }

        .cs-footer p {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.25);
          letter-spacing: 0.1em;
          font-weight: 300;
        }

        /* ── Fade-in helper ── */
        .fade-in-up {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }

        /* ── Responsive ── */
        @media (max-width: 1200px) {
          .cs-head-logo-left, .cs-head-logo-right { max-width: 100px; }
        }

        @media (max-width: 1024px) {
          .cs-head-logo-left, .cs-head-logo-right { max-width: 80px; }
          .cs-head-logo-left { left: 2%; }
          .cs-head-logo-right { right: 2%; }
        }

        @media (max-width: 768px) {
          .cs-hero-content { padding: 2rem 1.5rem; }
          .cs-logo { max-width: 300px; margin-top: 1rem; }
          .cs-tagline { font-size: clamp(2rem, 8vw, 3rem); }
          .cs-beta { padding: 5rem 1.5rem 6rem; }
          .cs-email-form {
            flex-direction: column;
            border-radius: 6px;
          }
          .cs-email-input {
            border-bottom: 1px solid rgba(255,255,255,0.08);
          }
          .cs-submit-btn { padding: 1rem; }
          .cs-bottom { padding: 5rem 2rem; }
          .cs-bottom-title { font-size: clamp(1.5rem, 5vw, 2rem); }
          .cs-head-logo-left { left: 1%; top: 55%; }
          .cs-head-logo-right { right: 1%; top: 55%; }
        }

        @media (max-width: 480px) {
          .cs-logo { max-width: 280px; }
        }
      `}</style>

      <div className="cs-root">
        {/* ── Hero ── */}
        <section className="cs-hero">
          <div className="cs-hero-bg" />
          <div className="cs-hero-overlay" />
          <div className="cs-hero-content">
            <img src={logo} alt="Rapdure" className="cs-logo" />

            <div className="cs-tagline-wrap">
              <h1 className="cs-tagline">Any Angle. Any Time. Everywhere</h1>
            </div>

            <button className="cs-scroll-hint" onClick={scrollToBeta}>
              <span>Join the Beta</span>
              <div className="cs-scroll-arrow" />
            </button>
          </div>
        </section>

        {/* ── Beta Signup ── */}
        <section className="cs-beta" ref={betaRef}>
          <div className="cs-beta-inner">
            <div className="cs-beta-label fade-in-up">Early Access</div>

            <h2 className="cs-beta-title fade-in-up">
              Be First to
              <br />
              <em>Experience rapdure</em>
            </h2>

            {!submitted ? (
              <div>
                <div className={`cs-email-form fade-in-up${error ? ' cs-error' : ''}`}>
                  <input
                    type="email"
                    className="cs-email-input"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoComplete="email"
                  />
                  <button type="button" className="cs-submit-btn" onClick={handleSubmit}>
                    Request Access
                  </button>
                </div>
                <p className="cs-privacy fade-in-up">
                  No spam, ever. We'll only reach out when it's time.
                </p>
              </div>
            ) : (
              <div className="cs-success">
                <div className="cs-check-icon">✓</div>
                <p className="cs-success-title">You're on the list</p>
                <span className="cs-success-sub">We'll be in touch when beta access is ready.</span>
              </div>
            )}
          </div>
        </section>

        <div className="cs-divider" />

        {/* ── Recording Reality ── */}
        <section className="cs-bottom">
          <img src={rapdureHeadLogo} alt="Rapdure Head" className="cs-head-logo-left" />
          <h2 className="cs-bottom-title">
            Recording Reality,
            <br />
            Not Imagination
          </h2>
          <img src={rapdureHeadLogo} alt="Rapdure Head" className="cs-head-logo-right" />
        </section>

        {/* ── Footer ── */}
        <footer className="cs-footer">
          <p>&copy; 2025 Rapdure. All rights reserved.</p>
        </footer>
      </div>
    </>
  )
}

export default ComingSoon
